import {CommunitiesManager} from 'community/community_follows.js';
import {Hospital} from 'hospitals/models/Hospital.js';
import {PracticeManager} from 'practices/practice_management.js';
import cookies from 'cookies.js';
import Deferred from 'deferred.js';
import events from 'events.js';
import http from 'http.js';
import ko from 'knockout';
import {loadData} from 'ko/utils.js';
import {page} from 'page.js';
import user from 'accounts/models/user.js';

import 'ko/elements/imagecropper.js';
import 'ko/elements/mceeditor.js';
import 'ko/elements/selectfilter.js';
import 'ko/validators.js';

class ProfessionalUser {
    constructor() {
        // Page 1 details
        this.id = ko.observable();
        this.profileId = ko.observable();
        this.passwordErrorMsg = ko.observable(null);
        this.password = ko.observable().extend({
            validation: {
                validator: () => {
                    if (this.passwordErrorMsg()) {
                        return false;
                    }
                    return true;
                },
                message: () => {
                    return this.passwordErrorMsg();
                },
            },
        });
        this.passwordConfirm = ko.observable().extend({
            maxLength: 255,
            required: true,
            equal: this.password,
        });
        this.email = ko.observable().extend({
            maxLength: 255,
            checkEmail: true,
            required: true,
            email: true,
        });
        this.username = ko.observable().extend({
            maxLength: 255,
            required: true,
        });
        this.emailConfirm = ko.observable().extend({
            maxLength: 255,
            required: true,
            equal: this.email,
        });
        this.title = ko.observable();
        this.firstName = ko.observable().extend({
            maxLength: 100,
            required: true,
        });
        this.lastName = ko.observable().extend({
            maxLength: 100,
            required: true,
        });
        this.specialties = ko.observableArray([]).extend({
            required: true,
        });
        this.tosAgree = ko.observable(false).extend({
            checked: true,
            required: true,
        });

        this.registrationErrors = ko.validation.group([
            this.password,
            this.passwordConfirm,
            this.email,
            this.emailConfirm,
            this.title,
            this.firstName,
            this.lastName,
            this.specialties,
            this.tosAgree,
        ]);

        // Page 3 details
        this.gender = ko.observable();
        this.interests = ko.observableArray([]);
        this.textSpecialInterests = ko.observable('');
        this.bio = ko.observable();
        this.registrationNumbers = ko.observable().extend({
            required: true,
        });
        this.qualifications = ko.observable();
        this.awards = ko.observable();
        this.hospitals = ko.observableArray([]);
        this.languages = ko.observableArray([]);

        this.detailErrors = ko.validation.group([
            this.gender,
            this.bio,
            this.registrationNumbers,
            this.qualifications,
            this.awards,
            this.hospitals,
            this.languages,
            this.interests,
            this.textSpecialInterests,
        ]);

        // Avatar
        this.avatar = ko.observable();
        this.avatarCrop = ko.observable();
        this.avatarUploaded = ko.observable(false);

        this.setAvatarUploaded = this.setAvatarUploaded.bind(this);
    }

    setAvatarUploaded() {
        this.avatarUploaded(true);
    }

    async save() {
        if (this.registrationErrors().length) {
            this.registrationErrors.showAllMessages();
            throw new Error('Registration errors');
        }
        let data = {
            password: this.password(),
            email: this.email(),
            title: this.title(),
            first_name: this.firstName(),
            last_name: this.lastName(),
        };
        const specialtySaves = [];

        const userData = await http.post({
            url: '/api/professionals/v1/register/',
            data,
        });
        this.id(userData.id);
        this.profileId(userData.profile_id);
        this.username(userData.username);

        user.loginUsername(this.username());
        user.loginPassword(this.password());

        await user.login();
        for (const specialty of this.specialties()) {
            const url = `/api/professionals/v1/${userData.profile_id}/specialties/`;
            data = {id: specialty.id()};
            specialtySaves.push(http.post({url, data}));
        }

        await Promise.all(specialtySaves);
    }

    async saveDetails() {
        if (this.detailErrors().length) {
            this.detailErrors.showAllMessages();
            throw new Error('Detail errors');
        }
        const saves = [];

        // Details
        const detailUrl = `/api/professionals/v1/${this.profileId()}/`;
        let data = {
            awards: this.awards(),
            bio: this.bio(),
            gender: this.gender(),
            interests_description: this.textSpecialInterests(),
            qualifications: this.qualifications(),
            registration_numbers: this.registrationNumbers(),
        };
        saves.push(http.patch({url: detailUrl, data}));

        // Languages
        for (const lang of this.languages()) {
            const url = `${detailUrl}languages/`;
            data = {id: lang.id()};
            saves.push(http.post({url, data}));
        }
        // Hospitals
        for (const hospital of this.hospitals()) {
            const url = `${detailUrl}hospitals/`;
            data = {id: hospital.id()};
            saves.push(http.post({url, data}));
        }
        // Special Interests
        for (const interest of this.interests()) {
            const url = `${detailUrl}interests/`;
            data = {id: interest.id()};
            saves.push(http.post({url, data}));
        }

        await Promise.all(saves);
    }
}

class Specialty {
    constructor() {
        this.id = ko.observable();
        this.name = ko.observable();
        this.professionalName = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
        this.professionalName(data.professional_name || data.name);
    }

    toString() {
        return `Specialty: ${this.name()}`;
    }
}

class SpecialInterest {
    constructor(manager) {
        this.manager = manager;
        this.id = ko.observable();
        this.name = ko.observable();
        this.specialty = ko.observable();

        this.longName = ko.pureComputed(() => {
            if (manager.user().specialties().length > 1) {
                const profName = this.specialty().professionalName();
                return `${profName} - ${this.name()}`;
            } else {
                return this.name();
            }
        });
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
        let specialty;
        for (const spec of this.manager.specialties()) {
            if (spec.id() === data.specialty.id) {
                specialty = spec;
                break;
            }
        }
        this.specialty(specialty);
    }

    toString() {
        return `Special Interest: ${this.specialty().name()} - ${this.name()}`;
    }
}

class State {
    constructor() {
        this.id = ko.observable();
        this.name = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
    }

    toString() {
        return `State: ${this.name()}`;
    }
}

class Language {
    constructor() {
        this.id = ko.observable();
        this.code = ko.observable();
        this.name = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.code(data.code);
        this.name(data.name);
    }
}

class Step {
    constructor(template, title, desc) {
        this.template = template;
        this.title = title;
        this.desc = desc;
    }
}

class SignupManager {
    constructor() {
        this.loading = ko.observable(false);
        this.saving = ko.observable(false);
        this.stepNum = ko.observable(0);
        this.titles = ko.observableArray([
            'Dr',
            'Prof',
            'A/Prof',
            'Adj A/Prof',
            'Clin A/Prof',
            'Mr',
            'Mrs',
            'Miss',
            'Ms',
        ]);
        this.genders = ['Female', 'Male', 'Other'];
        this.user = ko.observable(new ProfessionalUser());
        this.specialties = ko.observableArray();
        this.states = ko.observableArray();
        this.hospitals = ko.observableArray();
        this.interests = ko.observableArray();
        this.languages = ko.observableArray();
        this.practiceManager = new PracticeManager();
        this.practiceManager.addNew();
        this.communityManager = new CommunitiesManager();

        this.steps = [
            new Step('start', 'Create your HealthShare account'),
            new Step('practice', 'Provide details about your practice(s)'),
            new Step('details', 'Set up your professional profile'),
            new Step('avatar', 'Select a photo for your profile'),
            new Step(
                'topics',
                'Select Topics to Follow',
                `Select topics you are interested in. These will show up on
                your profile.`,
            ),
            new Step('done', 'Thank you for registering a profile'),
        ];
        this.step = ko.pureComputed(() => this.steps[this.stepNum()]);

        this.stepNum.subscribe((val) => {
            // On new page load, scroll back to top
            const header = document.getElementsByClassName('panel-heading')[0];
            header.scrollIntoView(true);

            function handler(evt) {
                let msg = 'Your registration is not yet complete.';
                msg += 'Really leave?';
                // IE, Firefox
                (evt || window.event).returnValue = msg;
                // WebKit
                return msg;
            }
            // After page 1 is saved ask user not to reload/navigate away etc
            if (val === 1) {
                window.onbeforeunload = handler;
            }
            // When signup done, remove message
            if (val === this.steps.length - 1) {
                window.onbeforeunload = null;
            }
        });

        this.nonEnglishLanguages = ko.pureComputed(() =>
            this.languages().filter((lang) => lang.name() !== 'English'),
        );

        this.candidateInterests = ko.pureComputed(() => {
            const specialties = this.user().specialties;
            const candidates = [];
            for (const interest of this.interests()) {
                if (specialties.indexOf(interest.specialty()) !== -1) {
                    candidates.push(interest);
                }
            }
            return candidates;
        });

        this.canEditBio = ko.pureComputed(() => {
            // Can't edit if not registered
            if (this.stepNum() === 0) {
                return false;
            }
            // Can't edit if no practices
            if (!this.practiceManager.practices().length) {
                return false;
            }
            // Can't edit if unsaved practice, otherwise good to go
            return !this.practiceManager.selectedPractice();
        });

        this.deferreds = {
            communities: new Deferred(),
            hospitals: new Deferred(),
            interests: new Deferred(),
            languages: new Deferred(),
            practices: new Deferred(),
            registered: new Deferred(),
            specialties: new Deferred(),
            states: new Deferred(),
        };

        this.loadTopics = this.loadTopics.bind(this);
        this.loadDone = this.loadDone.bind(this);
    }

    async load() {
        // For now to avoid sign up errors log user out on initial load
        try {
            await user.load();
            if (user.username()) {
                user.logout();
            }
        } catch (ex) {}

        loadData(
            '/api/professionals/v1/specialties/',
            {},
            this.specialties,
            Specialty,
            this,
            this.deferreds.specialties,
        );

        Promise.all([this.deferreds.specialties, events.ready()]).then(() => {
            ko.applyBindings(this);
        });

        Promise.all([
            this.deferreds.specialties,
            this.deferreds.registered,
        ]).then(() => {
            // Once page 1 pre reqs are loaded & user registered,
            // load page 2 prereqs
            Promise.all([this.practiceManager.load(false, false)]).then(
                this.deferreds.practices.resolve,
            );
        });

        this.deferreds.practices.then(() => {
            // Once practice manager is loaded, load pre-reqs for page 3
            loadData(
                '/api/directory/v1/states/',
                {},
                this.states,
                State,
                this,
                this.deferreds.states,
                [],
                'name',
            );
            loadData(
                '/api/professionals/v1/interests/',
                {'limit': 1000},
                this.interests,
                SpecialInterest,
                this,
                this.deferreds.interests,
                [this.deferreds.specialties],
            );
            loadData(
                '/api/hospitals/v1/hospitals/',
                {'limit': 1000},
                this.hospitals,
                Hospital,
                this,
                this.deferreds.hospitals,
                [this.deferreds.states],
                'name',
            );
            loadData(
                '/api/base/v1/languages/',
                {'limit': 1000},
                this.languages,
                Language,
                this,
                this.deferreds.languages,
            );
        });

        Promise.all([
            this.deferreds.languages,
            this.deferreds.interests,
            this.deferreds.hospitals,
            this.deferreds.registered,
        ]).then(() => {
            // Once page 3 pre reqs are loaded, load for page 5
            // (page 4 needs nothing)
            this.communityManager
                .load()
                .then(this.deferreds.communities.resolve);
        });
    }

    async register() {
        // only for page one
        this.saving(true);
        this.user().passwordErrorMsg(null);
        try {
            await this.user().save();
            this.deferreds.registered.resolve();
            this.loading(true);
            this.deferreds.practices.then(() => {
                this.loading(false);
            });
            this.stepNum(this.stepNum() + 1);
        } catch (ex) {
            const data = JSON.parse(JSON.stringify(ex.data));
            const errorMsg = data?.non_field_errors.join(' ');
            const isPasswordError = errorMsg.includes('password');
            this.user().passwordErrorMsg(errorMsg);

            if (!isPasswordError) {
                page.alert(
                    page.alertTypes.ERROR,
                    'An error occured saving your details. Please try again.',
                );
            }
        } finally {
            this.saving(false);
        }
    }

    editBio() {
        this.loading(true);
        Promise.all([
            this.deferreds.interests,
            this.deferreds.hospitals,
            this.deferreds.languages,
        ]).then(() => {
            this.loading(false);
            this.stepNum(this.stepNum() + 1);
        });
    }

    saveDetails() {
        this.saving(true);
        this.user()
            .saveDetails()
            .then(
                () => {
                    this.stepNum(this.stepNum() + 1);
                    this.saving(false);
                    // Update csrf token value which for some reason changes during
                    // this process
                    const csrftoken = cookies.get('csrftoken');
                    const csrfinput = document.querySelector(
                        'input[name=csrfmiddlewaretoken]',
                    );
                    csrfinput.value = csrftoken;
                },
                () => {
                    page.alert(
                        page.alertTypes.ERROR,
                        'Please fix form errors to continue.',
                    );
                    this.saving(false);
                },
            );
    }

    loadTopics() {
        this.loading(true);
        this.stepNum(this.stepNum() + 1);
        this.deferreds.communities.then(() => {
            this.loading(false);
        });
    }

    loadDone() {
        this.stepNum(this.stepNum() + 1);
        user.logout();
    }
}

new SignupManager().load();
